import React from "react";

import { Select } from "components/forms";
import { FormikProps } from "formik";
import { useTranslation } from "react-i18next";

import { FormValues } from "../schemas";

interface Props {
  formik: FormikProps<FormValues>;
  disabled?: boolean;
  showCustomIndexationOptions?: boolean;
}

const paymentTermItems = [
  { key: "fixed_principal", value: "fixed_principal", translationKey: "paymentTerm.fixedPrincipal" },
  { key: "fixed_total", value: "fixed_total", translationKey: "paymentTerm.fixedTotal" },
  { key: "custom", value: "custom", translationKey: "paymentTerm.custom" },
  { key: "balloon", value: "balloon", translationKey: "paymentTerm.balloon" },
  { key: "partial_balloon", value: "partial_balloon", translationKey: "paymentTerm.partialBalloon" },
  {
    key: "custom_with_principal_indexation",
    value: "custom_with_principal_indexation",
    translationKey: "paymentTerm.customWithIndexation"
  },
  {
    key: "interest_based_on_principal",
    value: "interest_based_on_principal",
    translationKey: "paymentTerm.interestBasedOnPrincipal"
  }
];

export const getPaymentTermLabel = (value: string, t: (key: string) => string): string => {
  const item = paymentTermItems.find((i) => i.value === value);
  return item ? t(item.translationKey) : value;
};

export const PaymentTerm: React.FC<Props> = ({ formik, disabled = false, showCustomIndexationOptions = true }) => {
  const { t } = useTranslation("debt");

  const handleChange = (value: string) => {
    if (value === "interest_based_on_principal") {
      formik.setFieldValue("principal_indexation_payment", false);
      formik.setFieldValue("interest_based_on_principal", true);
      formik.setFieldValue("payment_term", "custom");
    } else if (value === "custom_with_principal_indexation") {
      formik.setFieldValue("principal_indexation_payment", true);
      formik.setFieldValue("interest_based_on_principal", false);
      formik.setFieldValue("payment_term", "custom");
    } else {
      formik.setFieldValue("payment_term", value);
      formik.setFieldValue("principal_indexation_payment", false);
      formik.setFieldValue("interest_based_on_principal", false);
    }
  };

  const getDisplayValue = () => {
    if (formik.values.payment_term === "custom") {
      if (formik.values.principal_indexation_payment) {
        return "custom_with_principal_indexation";
      } else if (formik.values.interest_based_on_principal) {
        return "interest_based_on_principal";
      }
    }
    return formik.values.payment_term;
  };

  const baseItems = paymentTermItems.slice(0, 5);
  const extraItems = showCustomIndexationOptions ? paymentTermItems.slice(5) : [];
  const items = [...baseItems, ...extraItems].map((item) => ({ ...item, text: t(item.translationKey) }));

  return (
    <Select
      name="payment_term"
      label={t("paymentTerm.label")}
      variant="outlined"
      fullWidth
      value={getDisplayValue()}
      disabled={disabled}
      items={items}
      error={formik.touched.payment_term && Boolean(formik.errors.payment_term)}
      helperText={formik.touched.payment_term ? formik.errors.payment_term : undefined}
      onChange={(e) => handleChange(e.target.value as string)}
    />
  );
};